import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiCash, mdiCheckCircle, mdiAlertCircle } from '@mdi/js';
import BaseModal from './BaseModal';
import CustomFormInput from './forms/CustomFormInput';
import { formatCurrencyFromMinor } from '../utils/currency';
import './VenuePriceChangeModal.css';

/**
 * Venue-change payment adjustment modal (consent redesign).
 *
 * Charge (delta > 0): one modal, three consent paths —
 *   1. "Message student to confirm"  → posts the studio-edited draft into the booking chat (chat_message). Hidden for guests (no thread).
 *   2. "I've confirmed with student" → emails the pay link; gated on a consent checkbox (studio_confirmed).
 *   3. "Skip — no payment change"    → venue saved, no charge (skip).
 * Refund (delta < 0): simple confirm (issue refund + notify) or skip.
 *
 * fee/delta props are INTEGER CENTS → formatted via formatCurrencyFromMinor.
 * onConfirm(payload) is called with { action, path, ...extras }; the parent merges
 * newLocationId and performs the API call + toast.
 */
const VenuePriceChangeModal = ({
    isOpen,
    onClose,
    oldVenueName,
    newVenueName,
    oldFee,
    newFee,
    delta,
    currency,
    onConfirm,
    isProcessing,
    isGuest,
    defaultDraft,
}) => {
    const isRefund = delta < 0;
    const absCents = Math.abs(delta || 0);
    const formattedAmount = formatCurrencyFromMinor(absCents, currency);

    // Charge-path consent state (component mounts fresh each open — conditionally rendered by parent).
    const [selectedPath, setSelectedPath] = useState(null);
    const [draftMessage, setDraftMessage] = useState(defaultDraft || '');
    const [consentChecked, setConsentChecked] = useState(false);

    const chargeConfirmEnabled =
        (selectedPath === 'chat_message' && draftMessage.trim() !== '') ||
        (selectedPath === 'studio_confirmed' && consentChecked) ||
        selectedPath === 'skip';

    const handleChargeConfirm = () => {
        if (selectedPath === 'chat_message') {
            onConfirm({ action: 'charge', path: 'chat_message', draftMessage: draftMessage.trim() });
        } else if (selectedPath === 'studio_confirmed') {
            onConfirm({ action: 'charge', path: 'studio_confirmed', consentConfirmedCheckbox: true });
        } else if (selectedPath === 'skip') {
            onConfirm({ action: 'skip', path: 'skip' });
        }
    };

    const footer = isRefund ? (
        <>
            <button
                className="base-modal-cancel-btn venue-price-modal-dismiss-btn"
                onClick={() => onConfirm({ action: 'skip', path: 'skip' })}
                disabled={isProcessing}
            >
                Skip, no refund
            </button>
            <button
                className="base-modal-save-btn venue-price-modal-refund-btn"
                onClick={() => onConfirm({ action: 'refund', path: 'studio_confirmed' })}
                disabled={isProcessing}
            >
                <span className="venue-price-modal-btn-content">
                    <Icon path={mdiCheckCircle} size={0.8} />
                    {isProcessing ? 'Processing...' : `Issue refund of ${formattedAmount}`}
                </span>
            </button>
        </>
    ) : (
        <>
            <button
                className="base-modal-cancel-btn"
                onClick={onClose}
                disabled={isProcessing}
            >
                Cancel
            </button>
            <button
                className="base-modal-save-btn"
                onClick={handleChargeConfirm}
                disabled={isProcessing || !chargeConfirmEnabled}
            >
                <span className="venue-price-modal-btn-content">
                    <Icon path={mdiCheckCircle} size={0.8} />
                    {isProcessing ? 'Saving...' : 'Confirm'}
                </span>
            </button>
        </>
    );

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title="Venue change · payment difference"
            icon={mdiCash}
            iconColor="var(--dl-color-orange-default)"
            maxWidth={520}
            footer={footer}
            className="venue-price-modal-overlay-boost"
        >
            <div className="venue-price-modal-body">
                <div className="venue-price-modal-venues">
                    <div className="venue-price-modal-venue-row">
                        <span className="venue-price-modal-venue-label">Previous venue</span>
                        <span className="venue-price-modal-venue-name">{oldVenueName}</span>
                        <span className="venue-price-modal-fee">{formatCurrencyFromMinor(oldFee, currency)}</span>
                    </div>
                    <div className="venue-price-modal-venue-row venue-price-modal-venue-row--new">
                        <span className="venue-price-modal-venue-label">New venue</span>
                        <span className="venue-price-modal-venue-name">{newVenueName}</span>
                        <span className="venue-price-modal-fee">{formatCurrencyFromMinor(newFee, currency)}</span>
                    </div>
                </div>

                <div
                    className={`venue-price-modal-delta ${
                        isRefund ? 'venue-price-modal-delta--refund' : 'venue-price-modal-delta--charge'
                    }`}
                >
                    <Icon
                        path={isRefund ? mdiCheckCircle : mdiAlertCircle}
                        size={1}
                        color={isRefund ? 'var(--dl-color-green-default)' : 'var(--dl-color-orange-default)'}
                    />
                    <div className="venue-price-modal-delta-text">
                        <strong>
                            {isRefund
                                ? `Refund ${formattedAmount} to the student`
                                : `Collect an extra ${formattedAmount} from the student`}
                        </strong>
                        <p>
                            {isRefund
                                ? 'The new venue hire fee is lower. The difference is refunded to the original payment method and the student is notified.'
                                : 'The new venue hire fee is higher. Choose how to collect the difference below.'}
                        </p>
                    </div>
                </div>

                {!isRefund && (
                    <div className="venue-price-modal-paths">
                        {!isGuest && (
                            <div className="venue-price-modal-path-option">
                                <CustomFormInput
                                    type="radio"
                                    variant="purple"
                                    name="venue-adjust-path"
                                    label="Message in-app and email the payment link"
                                    value="chat_message"
                                    checked={selectedPath === 'chat_message'}
                                    onChange={() => setSelectedPath('chat_message')}
                                    disabled={isProcessing}
                                />
                                {selectedPath === 'chat_message' && (
                                    <div className="venue-price-modal-draft">
                                        <p className="venue-price-modal-draft-hint">
                                            This note is posted to the student's chat, and the secure payment link is emailed to them. Edit it or send as written.
                                        </p>
                                        <textarea
                                            className="venue-price-modal-draft-textarea"
                                            value={draftMessage}
                                            onChange={(e) => setDraftMessage(e.target.value)}
                                            rows={4}
                                            disabled={isProcessing}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="venue-price-modal-path-option">
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                name="venue-adjust-path"
                                label="I've already agreed this with the student"
                                value="studio_confirmed"
                                checked={selectedPath === 'studio_confirmed'}
                                onChange={() => setSelectedPath('studio_confirmed')}
                                disabled={isProcessing}
                            />
                            {selectedPath === 'studio_confirmed' && (
                                <div className="venue-price-modal-consent">
                                    <CustomFormInput
                                        type="checkbox"
                                        variant="purple"
                                        label="I confirm the student has agreed to this change and the extra payment."
                                        checked={consentChecked}
                                        onChange={(e) => setConsentChecked(e.target.checked)}
                                        disabled={isProcessing}
                                    />
                                    <p className="venue-price-modal-consent-hint">
                                        The student is emailed a payment link for the difference.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="venue-price-modal-path-option">
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                name="venue-adjust-path"
                                label="Skip, no payment change"
                                value="skip"
                                checked={selectedPath === 'skip'}
                                onChange={() => setSelectedPath('skip')}
                                disabled={isProcessing}
                            />
                        </div>
                    </div>
                )}
            </div>
        </BaseModal>
    );
};

export default VenuePriceChangeModal;
